import { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, ExternalLink, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero, GoldButton } from "@/components/Ui";
import { SERVICES, SEDI_OPTIONS, CONFIG } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "CAF e Patronato", "Ricorsi", "Formazione e certificazioni", "Altro"];
const today = new Date().toISOString().split("T")[0];
const empty = { nome: "", cognome: "", telefono: "", email: "", servizio: "", sede: "", giorno: "", fascia_oraria: "", note: "", corso: "" };

const Prenota = () => {
  const [params] = useSearchParams();
  const corso = params.get("corso") || "";
  const [form, setForm] = useState({ ...empty, corso, servizio: corso ? "Formazione e certificazioni" : "" });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.giorno) { setSlots([]); return; }
    setLoadingSlots(true); set("fascia_oraria", "");
    axios.get(`${API}/availability`, { params: { date: form.giorno } })
      .then((r) => setSlots(r.data.slots)).finally(() => setLoadingSlots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.giorno]);

  const submit = async (e) => {
    e.preventDefault();
    const req = ["nome", "cognome", "telefono", "email", "servizio", "sede", "giorno", "fascia_oraria"];
    if (req.some((k) => !form[k])) { toast.error("Compila tutti i campi obbligatori e scegli un orario."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true); toast.success("Richiesta inviata! Riceverai una email di conferma."); window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.status === 409 ? "Orario non più disponibile, scegline un altro." : "Errore nell'invio. Riprova.");
      if (err.response?.status === 409) { axios.get(`${API}/availability`, { params: { date: form.giorno } }).then((r) => setSlots(r.data.slots)); }
    } finally { setSubmitting(false); }
  };

  const renderSlots = () => {
    if (loadingSlots) return <p className="text-sm text-slate-500 mt-2">Caricamento orari…</p>;
    if (slots.length === 0) return <p className="text-sm text-rose-600 mt-2" data-testid="no-slots">Nessun orario disponibile per questa data. Scegli un altro giorno.</p>;
    return (
      <div className="mt-3 flex flex-wrap gap-2" data-testid="slots">
        {slots.map((s) => (
          <button type="button" key={s} data-testid={`slot-${s}`} onClick={() => set("fascia_oraria", s)}
            className={`px-4 py-2 text-sm font-semibold border transition-colors ${form.fascia_oraria === s ? "bg-navy-900 text-white border-navy-900" : "border-gray-200 text-navy-900 hover:border-gold-500"}`}>{s}</button>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="prenota-page">
      <PageHero eyebrow="Prenota appuntamento" title="Fissa la tua consulenza" subtitle="Scegli servizio, sede e uno degli orari disponibili. Riceverai una email di conferma." />
      <section className="bg-[#F8F9FA] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white border border-gray-100 shadow-crisp p-8 md:p-10">
            {done ? (
              <div className="text-center py-12" data-testid="prenota-success">
                <CheckCircle2 className="w-16 h-16 text-gold-500 mx-auto" />
                <h2 className="mt-6 font-serif text-3xl font-semibold text-navy-900">Richiesta ricevuta!</h2>
                <p className="mt-3 text-slate-600 max-w-md mx-auto">Grazie {form.nome}. Ti contatteremo per confermare l'appuntamento del <strong>{form.giorno}</strong> alle <strong>{form.fascia_oraria}</strong> presso <strong>{form.sede}</strong>.</p>
                <button onClick={() => { setForm(empty); setDone(false); }} className="mt-8 inline-flex items-center gap-2 bg-navy-900 text-white font-bold text-sm uppercase tracking-[0.1em] px-8 py-4 hover:bg-navy-500 transition-colors" data-testid="prenota-new-btn">Nuova prenotazione</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="prenota-form">
                {form.corso && <div className="bg-gold-500/15 border border-gold-500 px-4 py-3 text-sm text-navy-900" data-testid="prenota-corso-banner">Consulenza per il percorso: <strong>{form.corso}</strong></div>}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div><Label>Nome *</Label><Input data-testid="input-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                  <div><Label>Cognome *</Label><Input data-testid="input-cognome" value={form.cognome} onChange={(e) => set("cognome", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                  <div><Label>Telefono *</Label><Input data-testid="input-telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                  <div><Label>Email *</Label><Input type="email" data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div><Label>Tipo di servizio *</Label>
                    <Select value={form.servizio} onValueChange={(v) => set("servizio", v)}>
                      <SelectTrigger className="mt-2 rounded-none border-gray-200" data-testid="select-servizio"><SelectValue placeholder="Seleziona un servizio" /></SelectTrigger>
                      <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Sede *</Label>
                    <Select value={form.sede} onValueChange={(v) => set("sede", v)}>
                      <SelectTrigger className="mt-2 rounded-none border-gray-200" data-testid="select-sede"><SelectValue placeholder="Seleziona la sede" /></SelectTrigger>
                      <SelectContent>{SEDI_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Giorno *</Label><Input type="date" min={today} data-testid="input-giorno" value={form.giorno} onChange={(e) => set("giorno", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                {form.giorno && (
                  <div>
                    <Label>Orari disponibili *</Label>
                    {renderSlots()}
                  </div>
                )}
                <div><Label>Note</Label><Textarea data-testid="input-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} className="mt-2 rounded-none border-gray-200" placeholder="Descrivi la tua richiesta…" /></div>
                <GoldButton type="submit" disabled={submitting} className="w-full" data-testid="prenota-submit-btn"><CalendarCheck className="w-4 h-4" /> {submitting ? "Invio…" : "Conferma prenotazione"}</GoldButton>
              </form>
            )}
          </div>
          <aside className="space-y-6">
            <div className="bg-navy-900 text-white p-8">
              <h3 className="font-serif text-2xl font-semibold">Piattaforma GOLDIE</h3>
              <p className="mt-3 text-white/70 text-sm">Accedi per gestire pratiche e prenotazioni online.</p>
              <a href={CONFIG.goldieUrl} target="_blank" rel="noreferrer" data-testid="prenota-goldie-btn" className="mt-6 inline-flex items-center gap-2 bg-gold-500 text-navy-900 font-bold text-sm uppercase tracking-[0.1em] px-6 py-3 hover:bg-gold-400 transition-colors w-full justify-center">Accedi a GOLDIE <ExternalLink className="w-4 h-4" /></a>
            </div>
            <div className="bg-white border border-gray-100 p-8 shadow-crisp">
              <h3 className="font-serif text-xl font-semibold text-navy-900">Le nostre sedi</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">{SEDI_OPTIONS.map((s) => <li key={s} className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold-500" /> {s}</li>)}</ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Prenota;
